import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import SelfImprovementRoundedIcon from '@mui/icons-material/SelfImprovementRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Seo from '../SEO/Seo';

const USE_CASES = [
  {
    title: 'Job Rejection Recovery',
    summary:
      'Understand what went wrong in interviews, improve your narrative, and rebuild confidence for your next role.',
    category: 'Career',
    accent: '#4A3428',
    icon: <WorkOutlineRoundedIcon fontSize="small" />
  },
  {
    title: 'College and Grad School Setbacks',
    summary:
      'See how others handled admissions rejections and what steps helped them get accepted later.',
    category: 'Education',
    accent: '#1da58b',
    icon: <SchoolRoundedIcon fontSize="small" />
  },
  {
    title: 'Founder and Funding Rejections',
    summary:
      'Learn how founders reframed "no" from investors and iterated toward better pitches and traction.',
    category: 'Startup',
    accent: '#A67C42',
    icon: <RocketLaunchRoundedIcon fontSize="small" />
  },
  {
    title: 'Personal Rejection and Growth',
    summary:
      'Read stories about relationship setbacks and the practical habits people used to move forward.',
    category: 'Personal',
    accent: '#ef476f',
    icon: <SelfImprovementRoundedIcon fontSize="small" />
  },
  {
    title: 'Science Fair Rejections',
    summary:
      'Learn how students bounced back from science fair setbacks, improved projects, and came back stronger.',
    category: 'Science Fair',
    accent: '#118ab2',
    icon: <ScienceRoundedIcon fontSize="small" />
  },
  {
    title: 'Internship Application Setbacks',
    summary:
      'Explore how candidates improved resumes, interviews, and networking after internship rejections.',
    category: 'Internships',
    accent: '#ff9f1c',
    icon: <BusinessCenterRoundedIcon fontSize="small" />
  },
  {
    title: 'Summer Program Rejections',
    summary:
      'See how applicants adapted essays, portfolios, and timing to secure competitive summer program spots.',
    category: 'Summer Programs',
    accent: '#06d6a0',
    icon: <WbSunnyRoundedIcon fontSize="small" />
  }
];

const UseCases = () => {
  const { isAuthenticated } = useAuth();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Rejection Tales Use Cases',
    description:
      'Explore use cases for Rejection Tales across careers, education, startups, and personal growth.',
    url: `${window.location.origin}/use-cases`
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Seo
        title="Use Cases"
        description="Explore how people use Rejection Tales for job search resilience, admissions setbacks, startup rejection, and personal growth."
        path="/use-cases"
        schema={schema}
      />

      <Paper
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 4,
          mb: 4,
          background:
            'linear-gradient(135deg, rgba(139,111,92,0.18) 0%, rgba(74,52,40,0.08) 40%, rgba(255,255,255,0.96) 100%)',
          border: '1px solid rgba(74,52,40,0.12)',
          boxShadow: '0 14px 36px rgba(74,52,40,0.08)'
        }}
      >
        <Stack spacing={1}>
          <Typography
            variant="overline"
            sx={{ color: '#8B6F5C', fontWeight: 700, letterSpacing: 1.4 }}
          >
            Real-world applications
          </Typography>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            sx={{
              fontSize: { xs: '1.9rem', md: '2.2rem' },
              color: '#4A3428',
              fontFamily: '"Literata", Georgia, serif',
            }}
          >
            Rejection Tales Use Cases
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760, fontSize: '1rem' }}>
            Discover practical ways people use Rejection Tales to process setbacks,
            learn faster, and move forward with confidence. Public previews are open,
            and deeper stories are unlocked after login.
          </Typography>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {USE_CASES.map((item) => (
          <Grid item xs={12} md={6} key={item.title}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 30px rgba(0,0,0,0.12)'
                }
              }}
            >
              <Box
                sx={{
                  height: 6,
                  background: item.accent
                }}
              />
              <CardContent>
                <Chip
                  icon={item.icon}
                  label={item.category.toUpperCase()}
                  size="small"
                  sx={{
                    mb: 2,
                    backgroundColor: `${item.accent}18`,
                    color: item.accent,
                    fontWeight: 700
                  }}
                />
                <Typography variant="h6" component="h2" gutterBottom fontWeight={700}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.98rem' }}>
                  {item.summary}
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {isAuthenticated ? (
                  <Button
                    component={Link}
                    to="/tales"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ fontWeight: 700 }}
                  >
                    Read full stories
                  </Button>
                ) : (
                  <Button
                    component={Link}
                    to="/auth"
                    variant="contained"
                    startIcon={<LockIcon />}
                    sx={{
                      borderRadius: 20,
                      fontWeight: 700,
                      bgcolor: '#4A3428',
                      '&:hover': { bgcolor: '#3A281F' },
                    }}
                  >
                    Login to read more
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!isAuthenticated && (
        <Box
          mt={4}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(74,52,40,0.06)',
            border: '1px dashed rgba(74,52,40,0.28)'
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Public visitors can view use-case previews. Sign in to unlock deeper
            story details and full community content.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default UseCases;
